/**
 * Copyright 2022 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import { requestBaseURLGCPExtOrInt } from '@/config/serviceAPI';
import { type GlobalConfig, useGlobalConfig } from '@/features/globalConfig';
import { getAxios } from '@/lib/axios';
import { type QueryClient } from '@tanstack/react-query';
import type { CategoryFAQData, CategoryFAQResponse } from '../interface';
import { type AxiosInstance } from 'axios';
import { useLocale } from '@/hooks/useLocale';
import type { Locale } from '@/types/i18n';
import { createQuery } from '@/utils/createReactQuery';
import { type GetFaqUsingGetParamsModel } from '@/api/types/seo-data-types';
import { getCountryFromLocale } from '@/utils/getCountryFromLocale';
import environmentConfig from '@/config/environment';

const URL = `${requestBaseURLGCPExtOrInt}/sls/b2c/product-discovery-seo-data-bs/v2/faqs`;

type SEORepositoryType = 'CATEGORY' | 'PRODUCT';

type CategoryFAQQueryOptions = {
  locale: Locale;
  seoRepositoryType: SEORepositoryType;
  categoryId: string | null | undefined;
};

const newSelector = (data: CategoryFAQResponse): CategoryFAQData => data;

const { useData: useFAQ, prefetch: prefetchFAQ } = createQuery<
  CategoryFAQData,
  CategoryFAQQueryOptions
>('faq', async (options, axiosInstance) => getCategoryFAQ({ ...options }, axiosInstance));

const getCategoryFAQ = async (options: CategoryFAQQueryOptions, axiosInstance?: AxiosInstance) => {
  const { categoryId, seoRepositoryType, locale } = options;
  const customerType = 'B2C';
  const country = getCountryFromLocale(locale);

  if (categoryId == null) {
    throw new Error("A 'categoryId' was not passed");
  }

  const params: GetFaqUsingGetParamsModel = {
    country,
    customerType,
    seoRepositoryId: categoryId,
    seoRepositoryType,
  };

  const response = await getAxios(axiosInstance).get<CategoryFAQResponse>(URL, {
    params,
    withCredentials: !environmentConfig.DISABLE_GCP_API_CREDENTIALS,
  });

  return newSelector(response.data);
};

const isEnabled = (config: GlobalConfig | undefined, seoPageType: SEORepositoryType) => {
  const conf = config?.FAQ_CATEGORY_SHELF_ENABLED
    ? config.FAQ_CATEGORY_SHELF_ENABLED
    : 'false-false';

  const [categoryEnabled, shelfEnabled] = conf.split('-').map((el) => {
    const result: unknown = JSON.parse(el);

    if (typeof result !== 'boolean') {
      throw new Error('parsing error');
    }
    return result;
  });

  if (
    (seoPageType === 'CATEGORY' && !categoryEnabled) ||
    (seoPageType === 'PRODUCT' && !shelfEnabled)
  ) {
    return false;
  }

  return true;
};

export const useCategoryFAQ = ({
  enabled = true,
  categoryId,
  seoRepositoryType,
}: {
  enabled: boolean;
  categoryId: string | null | undefined;
  seoRepositoryType: SEORepositoryType;
}) => {
  const configResult = useGlobalConfig();
  const locale = useLocale();

  return useFAQ({
    locale,
    categoryId,
    seoRepositoryType,
    enabled:
      enabled &&
      Boolean(categoryId) &&
      configResult.isSuccess &&
      isEnabled(configResult.data, seoRepositoryType),
  });
};

export const prefetchCategoryFAQ = async (
  axiosInstance: AxiosInstance,
  locale: Locale,
  queryClient: QueryClient,
  options: {
    categoryId: string | null | undefined;
    seoRepositoryType: SEORepositoryType;
  },
  config: GlobalConfig | undefined,
) => {
  if (!isEnabled(config, options.seoRepositoryType)) {
    return;
  }

  return prefetchFAQ(queryClient, { ...options, locale }, axiosInstance);
};
